import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Briefcase, Settings2, Fuel, ArrowRight, Car as CarIcon } from "lucide-react";
import { api, money } from "@/lib/api";
import { SearchWidget } from "@/components/SearchWidget";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function Cars() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const city = params.get("city") || "";
  const category = params.get("category") || "all";
  const [loading, setLoading] = useState(true);
  const [cars, setCars] = useState([]);

  useEffect(() => {
    setLoading(true);
    api.get("/cars/search", { params: { city, category } }).then(({ data }) => setCars(data.results)).finally(() => setLoading(false));
  }, [city, category]);

  const book = (c) => navigate("/checkout", { state: { item: {
    type: "car", title: `${c.name} · ${c.category}`, amount: c.price_per_day, currency: "USD",
    details: { car: c.name, category: c.category, city, supplier: c.supplier, seats: c.seats, transmission: c.transmission, image: c.image, per_day: c.price_per_day },
  } } });

  return (
    <div>
      <div className="bg-gradient-to-br from-brand to-[#FF0080] grain relative pt-8 pb-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto"><SearchWidget defaultTab="cars" /></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-14 relative">
        <h1 className="font-heading font-black text-2xl text-white mb-6">{city ? `Car hire in ${city}` : "Car hire"}</h1>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />) :
            cars.length === 0 ? (
              <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <CarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No vehicles for this filter.</p>
              </div>
            ) : cars.map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} data-testid={`car-card-${i}`}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all">
                <div className="relative h-44 bg-slate-100 overflow-hidden">
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-slate-950/80 backdrop-blur text-white text-xs font-bold">{c.category}</span>
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-bold text-lg text-slate-950">{c.name}</h3>
                  <div className="text-xs text-slate-400">or similar · {c.supplier}</div>
                  <div className="grid grid-cols-2 gap-2 mt-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-slate-400" />{c.seats} seats</span>
                    <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-slate-400" />{c.bags} bags</span>
                    <span className="flex items-center gap-1.5"><Settings2 className="w-4 h-4 text-slate-400" />{c.transmission}</span>
                    <span className="flex items-center gap-1.5"><Fuel className="w-4 h-4 text-slate-400" />Full-to-full</span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-end justify-between">
                    <div><span className="text-xs text-slate-400">per day</span><div className="font-heading font-black text-2xl text-slate-950">{money(c.price_per_day)}</div></div>
                    <Button onClick={() => book(c)} data-testid={`book-car-${i}`} className="rounded-full bg-coral hover:bg-coral-hover gap-1">Book<ArrowRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              </motion.div>
            ))}
        </div>
      </div>
    </div>
  );
}
